import {
  activeDeliveryItems,
  deliveryStateSource,
  offerOptionsFromStoredRows,
  resolveStoneHandlingMode,
  stoneHandlingModeLabel,
  summarizeDeliveryState,
  type AssessmentRow,
  type AssessmentSettlementType,
  type DeliveryMethod,
  type DeliveryState,
  type MailOrder,
  type StoreCheckin,
} from "@gull/shared";

export type SettlementType = AssessmentSettlementType;

/**
 * Both kinds render as the same card, so each one is flattened to this first. Every part of the
 * offer follows the state machine's `offerVisible`, so a draft assessment stays hidden but an
 * answered offer keeps showing its amount; `canRespond` is the narrower "and it is still unanswered".
 */
export interface Delivery {
  deliveryMethod: DeliveryMethod;
  /** The document id within its own collection — unique only together with `deliveryMethod`. */
  id: string;
  title: string;
  createdAt: number;
  reference: string | null;
  itemCount: number;
  imagePath: string | null;
  assessmentRows: AssessmentRow[];
  totalOffer: number | null;
  /**
   * The stone-removal charge the offer was priced with. Part of the offer, not of the karat rows,
   * so it follows `totalOffer`'s visibility: `null` until the offer is out.
   */
  deductionNok: number | null;
  stoneHandling: string | null;
  state: DeliveryState;
  canRespond: boolean;
  settlementType: SettlementType;
  goldValue: number | null;
  offerForSettlement: (settlementType: SettlementType) => number;
}

function assessmentOfferForSettlement(
  source: {
    assessmentRows?: AssessmentRow[] | null;
    assessmentTotalValue?: number | null;
    assessmentPayoutValueOverrideNok?: number | null;
    assessmentGiftCardValueOverrideNok?: number | null;
    assessmentDeductionNok?: number | null;
  },
  settlementType: SettlementType,
) {
  const options = offerOptionsFromStoredRows({
    rows: source.assessmentRows,
    payoutOverride: source.assessmentPayoutValueOverrideNok,
    giftCardOverride: source.assessmentGiftCardValueOverrideNok,
    deductionNok: source.assessmentDeductionNok,
  });
  return options?.[settlementType] ?? source.assessmentTotalValue ?? 0;
}

/**
 * Everything the two kinds agree on — the discriminant included, now that the record carries it.
 * What survives in each mapper below is exactly what differs: its id and what it is called.
 */
function sharedDeliveryFields(record: MailOrder | StoreCheckin): Omit<Delivery, "id" | "title"> {
  const source = deliveryStateSource(record);
  const state = summarizeDeliveryState(source);
  const stoneMode = resolveStoneHandlingMode(record);
  return {
    deliveryMethod: record.deliveryMethod,
    createdAt: record.createdAt,
    reference: record.receiptCode,
    itemCount: activeDeliveryItems(record).length,
    imagePath: record.orderImageUrls?.[0] ?? null,
    assessmentRows: source.offerVisible ? (record.assessmentRows ?? []) : [],
    totalOffer: source.offerVisible ? (record.assessmentTotalValue ?? null) : null,
    deductionNok: source.offerVisible ? (record.assessmentDeductionNok ?? null) : null,
    stoneHandling: stoneMode == null ? null : stoneHandlingModeLabel(stoneMode),
    state,
    // The state machine already knows what "an offer is out and unanswered" means, so neither kind
    // gets to spell it out again. Without a receipt code there is nothing to answer against —
    // useDeliveryActions posts to /deliveries/status/{reference}.
    canRespond: state.code === "OFFER_SENT" && record.receiptCode != null,
    settlementType: record.assessmentSettlementType ?? "PAYOUT",
    goldValue: source.offerVisible ? (record.assessmentGoldValue ?? null) : null,
    offerForSettlement: (settlementType) => assessmentOfferForSettlement(record, settlementType),
  };
}

export function mailOrderDelivery(order: MailOrder): Delivery {
  return {
    ...sharedDeliveryFields(order),
    id: order.id,
    title: "Gullkonvolutt - innsending via post",
  };
}

export function checkinDelivery(checkin: StoreCheckin): Delivery {
  return {
    ...sharedDeliveryFields(checkin),
    id: checkin.id,
    title: "Innlevering i butikk",
    // Staff can create a check-in before it has a receipt code, and the id is the only handle the
    // customer can quote until it does. Answering an offer still requires the code itself.
    reference: checkin.receiptCode ?? checkin.id,
  };
}
